import { createPortal } from "react-dom";
import { LogoMark } from "./Logo";

const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });
const kg = (n) => Number(n || 0).toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const vendorLabel = (nama, singkatan) => singkatan ? `${nama} (${singkatan})` : nama || "—";
const stamp = () => new Date().toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

/**
 * Overlay cetak — dirender lewat portal langsung di <body> sehingga saat print hanya overlay ini yang tampil
 * (sidebar/header aplikasi disembunyikan via CSS `body > *:not(.print-overlay)`), tanpa offset layout.
 * pageSize: "A4" | "A4 landscape" | "<w>mm <h>mm" (stiker)
 */
export function PrintOverlay({ onClose, children, testid, onPrint, toolbar, landscape = false, pageSize, maxWidth }) {
  const size = pageSize || (landscape ? "A4 landscape" : "A4");
  const margin = pageSize && !/^A4/i.test(pageSize) ? "0" : "10mm";
  return createPortal(
    <div className="print-overlay fixed inset-0 z-[70] overflow-y-auto bg-black/75 p-4" data-testid={testid}>
      <style>{`@media print { @page { size: ${size}; margin: ${margin}; } }`}</style>
      <div className="sticky top-3 z-10 mx-auto flex w-fit flex-wrap items-center gap-2 print:hidden">
        {toolbar}
        <button onClick={() => { onPrint?.(); window.print(); }} className="rounded bg-sky-600 px-5 py-2 text-sm font-semibold text-white shadow-lg hover:bg-sky-500" data-testid={`${testid}-print-button`}>
          Cetak / 打印
        </button>
        <button onClick={onClose} className="rounded bg-slate-700 px-5 py-2 text-sm text-white shadow-lg hover:bg-slate-600" data-testid={`${testid}-close-button`}>
          Tutup
        </button>
      </div>
      <div className={`print-area mx-auto my-4 w-full bg-white text-black shadow-2xl print:my-0 print:max-w-none print:shadow-none ${maxWidth || (landscape ? "max-w-[297mm]" : "max-w-[210mm]")}`}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

const TH = ({ children, className = "" }) => <th className={`text-[9px] font-bold ${className}`}>{children}</th>;
const TD = ({ children, className = "" }) => <td className={`text-[10px] ${className}`}>{children}</td>;

const priceOf = (m, spk) => {
  const unit = (m.unitHarga || "TON").toUpperCase().startsWith("M") ? "METER" : "TON";
  const harga = m.hargaSatuan != null ? Number(m.hargaSatuan) : Number(spk.rateTon || 0);
  return { unit, harga };
};
export const amountOf = (qty, beratUnitKg, panjangMm, unit, harga) =>
  unit === "METER" ? Math.round(qty * ((Number(panjangMm) || 0) / 1000) * harga) : Math.round((beratUnitKg * qty / 1000) * harga);

export function SPKPrintView({ spk, markings, user, commercial = true }) {
  const rows = (markings || []).map((m, i) => {
    const beratUnit = (m.unitTon || 0) * 1000;
    const totalBerat = (m.qty || 0) * beratUnit;
    const { unit, harga } = priceOf(m, spk);
    const totalHarga = amountOf(m.qty || 0, beratUnit, m.panjang, unit, harga);
    return { no: i + 1, code: m.code, qty: m.qty, panjang: m.panjang, beratUnit, totalBerat, totalHarga, unit, harga, profile: m.profile || spk.profile, keterangan: m.keterangan || "" };
  });
  const hasPrice = commercial && rows.some((r) => r.harga > 0);
  const terlambat = spk.tanggalSelesai && spk.status === "Aktif" ? Math.max(0, Math.floor((new Date() - new Date(spk.tanggalSelesai)) / 86400000)) : 0;
  const totQty = rows.reduce((a, r) => a + (r.qty || 0), 0);
  const totTon = rows.reduce((a, r) => a + r.totalBerat, 0) / 1000;
  const totNilai = rows.reduce((a, r) => a + r.totalHarga, 0);
  const hari = spk.tanggal && spk.tanggalSelesai
    ? Math.max(0, Math.round((new Date(spk.tanggalSelesai) - new Date(spk.tanggal)) / 86400000)) : 0;
  const status = spk.status === "Aktif" ? "TERBIT / 已发布" : "DRAFT / 草稿";

  return (
    <div className="print-sheet" data-testid="spk-print-sheet">
      <div className="flex items-start justify-between border-b-2 border-black pb-3">
        <div className="flex items-center gap-3">
          <LogoMark size={54} />
          <div className="leading-tight">
            <div className="font-display text-[15px] font-bold">PT HONGLU BAJA INDONESIA</div>
            <div className="text-[10px]">Subsidiary of Honglu Group</div>
            <div className="text-[10px]">Steel Structure Solution / <span className="cn">钢结构解决方案</span></div>
          </div>
        </div>
        <div className="text-right leading-tight">
          <div className="font-display text-[15px] font-bold">Honglu Group</div>
          <div className="text-[10px] italic">Build a Better Tomorrow</div>
          <div className="mt-1 inline-flex items-center gap-2">
            {spk.menggantikanSpk && <span className="font-mono text-[11px] text-gray-500 line-through decoration-red-600 decoration-2" data-testid="spk-print-old-no">{spk.menggantikanSpk}</span>}
            <span className="inline-block bg-gray-200 px-2 py-1 font-mono text-[11px] font-bold">{spk.no}</span>
          </div>
        </div>
      </div>

      <div className="relative mt-4 text-center">
        <div className="font-display text-xl font-black tracking-wide">SURAT PERINTAH KERJA (SPK)</div>
        <div className="cn text-[11px]">工作指令</div>
        {spk.menggantikanSpk ? (
          <div className="absolute left-0 top-0 -rotate-6 border-[3px] border-red-600 px-2 py-0.5 font-display text-[14px] font-black tracking-widest text-red-600" data-testid="spk-print-revisi-stamp">REVISI <span className="cn text-[10px]">修订</span></div>
        ) : null}
        <div className="absolute right-0 top-0 flex gap-1">
          <span className="border border-black px-1.5 py-0.5 text-[9px] font-bold" data-testid="spk-print-doc-type">{commercial ? "KOMERSIAL / 商务版" : "OPERASIONAL / 作业版"}</span>
          <span className="border border-black px-1.5 py-0.5 text-[9px] font-bold">{spk.menggantikanSpk ? "REVISI / 修订版" : "ASLI / 原件"}</span>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-4">
        <div className="space-y-1 text-[10px]">
          <div><b>Nomor SPK / <span className="cn">SPK编号</span>:</b> <span className="font-mono">{spk.no}</span></div>
          <div><b>Menggantikan SPK / <span className="cn">替代SPK</span>:</b> {spk.menggantikanSpk ? <span className="font-mono line-through decoration-red-600">{spk.menggantikanSpk}</span> : "—"}{spk.menggantikanSpk && <span className="ml-1 text-[9px] font-bold text-red-600">(dibatalkan / 作废)</span>}</div>
          <div><b>Proyek / <span className="cn">项目</span>:</b> {spk.projectKode ? `${spk.projectKode} - ` : ""}{spk.projectName}</div>
          <div><b>Vendor / <span className="cn">供应商</span>:</b> {vendorLabel(spk.vendorName, spk.vendorSingkatan)}</div>
          <div><b>Area / <span className="cn">区域</span>:</b> {spk.area}</div>
        </div>
        <div className="space-y-1 text-[10px]">
          <div><b>Jenis Pekerjaan / <span className="cn">工种</span>:</b> {spk.jenisPekerjaan || "FABRIKASI"} / <span className="cn">制造</span></div>
          <div><b>Tanggal Mulai / <span className="cn">开始日期</span>:</b> {spk.tanggal || "—"}</div>
          <div><b>Target Selesai / <span className="cn">目标完成日期</span>:</b> {spk.tanggalSelesai || "—"}</div>
          <div><b>Status / <span className="cn">状态</span>:</b> {status}</div>
          <div><b>Target Hari / <span className="cn">目标天数</span>:</b> {hari} hari / <span className="cn">天</span></div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-4">
        <div className="border border-black p-2 text-[10px] leading-relaxed">
          <b>Pemberi Tugas / <span className="cn">发包方</span></b><br />
          Perusahaan / <span className="cn">公司</span>: PT Honglu Baja Indonesia<br />
          Admin / <span className="cn">管理员</span>: {user?.name || "Workshop Administrator"}
        </div>
        <div className="border border-black p-2 text-[10px] leading-relaxed">
          <b>Penerima Tugas / <span className="cn">承包方</span></b><br />
          Vendor / <span className="cn">供应商</span>: {vendorLabel(spk.vendorName, spk.vendorSingkatan)}<br />
          Area / <span className="cn">区域</span>: {spk.area}
        </div>
      </div>

      <table className="mt-3">
        <thead>
          <tr className="bg-gray-100">
            <TH className="w-8">No</TH>
            <TH>Marking <span className="cn">构件编号</span></TH>
            <TH>Profile <span className="cn">型钢规格</span></TH>
            <TH>Qty <span className="cn">数量</span></TH>
            <TH>Panjang (mm) <span className="cn">长度</span></TH>
            <TH>Berat Unit (kg) <span className="cn">单重</span></TH>
            <TH>Total Berat (kg) <span className="cn">总重量</span></TH>
            {commercial && <TH>Harga Satuan <span className="cn">单价</span></TH>}
            {commercial && <TH>Total Harga <span className="cn">总价</span></TH>}
            <TH>Keterangan <span className="cn">备注</span></TH>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.no}>
              <TD className="text-center">{r.no}</TD>
              <TD className="font-mono font-bold">{r.code}</TD>
              <TD>{r.profile}</TD>
              <TD className="text-right font-mono">{r.qty}</TD>
              <TD className="text-right font-mono">{r.panjang != null ? r.panjang.toLocaleString("id-ID", { maximumFractionDigits: 2 }) : "—"}</TD>
              <TD className="text-right font-mono">{kg(r.beratUnit)}</TD>
              <TD className="text-right font-mono">{kg(r.totalBerat)}</TD>
              {commercial && <TD>{r.harga ? <>{r.unit === "METER" ? <>PER METER / <span className="cn">按米</span></> : <>PER TON / <span className="cn">按吨</span></>}<br />{rp(r.harga)}</> : "—"}</TD>}
              {commercial && <TD className="text-right font-mono">{r.harga ? rp(r.totalHarga) : "—"}</TD>}
              <TD>{r.keterangan}</TD>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={`mt-2 grid gap-2 text-[10px] ${commercial ? "grid-cols-4" : "grid-cols-3"}`}>
        <div className="border border-black p-1.5"><b>Total Qty / <span className="cn">总数量</span></b><br /><span className="font-mono font-bold">{totQty}</span></div>
        <div className="border border-black p-1.5"><b>Total Tonase / <span className="cn">总吨位</span></b><br /><span className="font-mono font-bold">{kg(totTon)} ton</span></div>
        {commercial && <div className="border border-black p-1.5"><b>Total Nilai SPK / <span className="cn">SPK总金额</span></b><br /><span className="font-mono font-bold">{hasPrice ? rp(totNilai) : "—"}</span></div>}
        <div className="border border-black p-1.5"><b>Hari Keterlambatan / <span className="cn">延误天数</span></b><br /><span className={terlambat ? "font-bold text-red-700" : ""}>{terlambat} hari / <span className="cn">天</span></span></div>
      </div>

      <div className="mt-3 text-[10px]">
        <b>Ketentuan / <span className="cn">说明</span></b>
        <ol className="mt-1 list-decimal space-y-0.5 pl-5">
          <li>Pekerjaan harus mengacu pada shop drawing yang disetujui. / <span className="cn">所有工作必须依据已批准的深化图纸进行。</span></li>
          <li>Progress resmi dihitung setelah Final Check Material Control. / <span className="cn">正式进度以物料控制最终检查后计算。</span></li>
          <li>Perubahan Qty atau lingkup setelah SPK terbit harus menggunakan SPK baru. / <span className="cn">SPK发布后如变更数量或范围，必须签发新的SPK。</span></li>
        </ol>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-4 text-[10px]">
        {[["Vendor / 供应商", vendorLabel(spk.vendorName, spk.vendorSingkatan)], ["Kepala Area / 区域负责人", ""], ["Kepala Pabrik / 厂长", ""]].map(([t, sub]) => (
          <div key={t} className="sig-block text-center">
            <div className="font-bold">{t}</div>
            {sub && <div className="text-[9px]">{sub}</div>}
            <div className="h-14" />
            <div className="border-t border-black pt-0.5 text-left pl-2">Nama / <span className="cn">姓名</span>: ________</div>
            <div className="mt-1 text-left pl-2">Tanggal / <span className="cn">日期</span>: ________</div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex justify-between text-[9px] text-gray-500">
        <span>HONGLU FABRIKASI SISTEM</span>
        <span>{stamp()}</span>
      </div>
    </div>
  );
}

export function STBPrintView({ stb, user }) {
  const rows = stb.markings || [];
  const spkNos = [...new Set(rows.map((r) => r.spkNo).filter(Boolean))].join(", ") || "—";
  return (
    <div className="print-sheet" data-testid="stb-print-sheet">
      <div className="grid grid-cols-3 items-start gap-3 border-b-2 border-black pb-3">
        <div className="flex items-center gap-2">
          <LogoMark size={46} />
          <div className="leading-tight">
            <div className="font-display text-[12px] font-bold">PT HONGLU BAJA INDONESIA</div>
            <div className="cn text-[9px]">宏睿钢印印尼有限公司</div>
          </div>
        </div>
        <div className="text-center leading-tight">
          <div className="font-display text-3xl font-black tracking-widest">STB{stb.partial && <span className="ml-2 rounded border-2 border-red-700 px-2 align-middle text-sm font-bold tracking-widest text-red-700" data-testid="stb-print-partial-badge">PARSIAL</span>}</div>
          <div className="font-display text-[11px] font-bold">FORM SERAH TERIMA FABRIKASI{stb.partial ? " — SEBAGIAN MARKING" : ""}</div>
          <div className="cn text-[10px]">加工交接表{stb.partial ? "（部分构件）" : ""}</div>
          {stb.partial && <div className="text-[9px] text-red-700">{stb.partialInfo} · marking lain pada STB ini tidak dicetak</div>}
        </div>
        <div className="space-y-0.5 text-[9px]">
          <div><b>No. STB:</b> <span className="font-mono font-bold">{stb.no}</span></div>
          <div><b>No. SPK:</b> <span className="font-mono">{spkNos}</span></div>
          <div><b>Tanggal / <span className="cn">日期</span>:</b> {stb.tanggal}</div>
          <div><b>Vendor / <span className="cn">供应商</span>:</b> {vendorLabel(stb.vendorName, stb.vendorSingkatan)}</div>
          <div><b>Area / <span className="cn">区域</span>:</b> {stb.area || "—"}</div>
          <div><b>Revision / <span className="cn">修订</span>:</b> {stb.revision || 1}{stb.status === "Sebagian" && <span className="ml-1 font-bold text-red-700">(cetak ulang sisa / 补打剩余)</span>}</div>
        </div>
      </div>

      <div className="mt-2 flex justify-between text-[10px]">
        <div><b>Dikeluarkan oleh / <span className="cn">签发人</span> :</b> {stb.by || user?.name || "Admin"}</div>
        <div><b>Diterima oleh / <span className="cn">接收方</span> :</b> {stb.area ? `Area ${stb.area}` : "________"}</div>
      </div>

      <table className="mt-2">
        <thead>
          <tr className="bg-gray-100">
            <TH className="w-8">No</TH>
            <TH>Marking / <span className="cn">构件编号</span></TH>
            <TH>Profile / <span className="cn">型钢规格</span></TH>
            <TH>Total Kebutuhan / <span className="cn">总需求量</span></TH>
            <TH>Sudah Selesai Sebelumnya / <span className="cn">之前已完成</span></TH>
            <TH>Qty STB Ini / <span className="cn">本次数量</span></TH>
            <TH>Selesai Hari Ini / <span className="cn">今日完成（手写）</span></TH>
            <TH>Keterangan / <span className="cn">备注</span></TH>
          </tr>
        </thead>
        <tbody>
          {rows.map((m, i) => (
            <tr key={m.id || i}>
              <TD className="text-center">{i + 1}</TD>
              <TD className="font-mono font-bold">{m.code}</TD>
              <TD>{m.profile || "—"}</TD>
              <TD className="text-center font-mono">{m.qtyTotal ?? "—"}</TD>
              <TD className="text-center font-mono" data-testid={`stb-print-before-${m.code}`}>{(m.stbBefore ?? 0) + (m.realisasi || 0)}</TD>
              <TD className="text-center font-mono">{Math.max(0, (m.qty || 0) - (m.realisasi || 0))}{(m.realisasi || 0) > 0 && <span className="ml-1 text-[8px] text-gray-500">(dari {m.qty})</span>}</TD>
              <TD className="h-6" />
              <TD className="text-[8px] text-gray-500">{(m.realisasi || 0) > 0 ? `realisasi ${m.realisasi}` : ""}</TD>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 grid grid-cols-5 gap-2 text-[8px]">
        {["Vendor / 供应商", "QC / 质检", "Kepala Area / 区域负责人", "Penerima / 接收人", "Final Check Material Control / 物控最终检查"].map((t, i) => (
          <div key={t} className="sig-block text-center leading-relaxed">
            <div className="font-bold">{t}</div>
            {i === 0 ? <div className="truncate text-[8px] text-gray-700">{vendorLabel(stb.vendorName, stb.vendorSingkatan)}</div> : <div className="h-3" />}
            <div className="h-9" />
            <div className="border-t border-black pt-0.5 text-left pl-1">Nama / <span className="cn">姓名</span>:</div>
            <div className="mt-1 text-left pl-1">Tanggal / <span className="cn">日期</span>:</div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex justify-between text-[9px] text-gray-500">
        <span>1 dari 1</span>
        <span>{stamp()}</span>
      </div>
    </div>
  );
}